import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'react-use';

import PopupNotification from '@/domain/core/PopupNotification';
import User from '@/domain/core/User';
import { Button } from '@/ui/Button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/ui/InputOTP';
import { Modal } from '@/ui/modals';
import { backendApi } from '@/utils/api';
import { Icon } from '@/ui/Icon';

const ERROR_CODES = {
	403: 403,
};

interface IProps {
	isOpen: boolean;
	onCloseHandler: () => void;
	onSuccess?: () => void;
	onError?: () => void;
	codeLength?: number;
}

interface ICodeConfirmTimeout {
	nextTryToSendInSeconds: number;
}

interface ICodeDelayTimeout {
	nextTryToConfirmInSeconds?: number;
}

const ConfirmEmailModal = ({ isOpen, onCloseHandler, onSuccess, onError, codeLength = 4 }: IProps) => {
	const { t } = useTranslation();

	const [isCodeBlocked, setIsCodeBlocked] = useToggle(false);
	const [isAgainBlocked, setIsAgainBlocked] = useToggle(true);
	const [pinCode, setPinCode] = useState<string>();
	const [codeRequestDelay, setCodeRequestDelay] = useState<number>(0);

	const closeHandler = useCallback(() => {
		setPinCode('');
		setIsCodeBlocked(false);

		onCloseHandler();
	}, []);

	const handlePinRequest = async () => {
		if (codeRequestDelay) return;
		setIsAgainBlocked(true);
		setPinCode('');
		try {
			const { data: res } = await backendApi.get<ICodeConfirmTimeout>('/v2/users/confirm');
			setCodeRequestDelay(res.nextTryToSendInSeconds);
			PopupNotification.emitter('success', t('emailConfirm.enterPin'));
		} catch (e) {
			const { response } = e as AxiosError;
			if (response?.status === ERROR_CODES[403]) {
				setCodeRequestDelay(60);
				PopupNotification.emitter('info', t('emailConfirm.enterPin'));
				return;
			}
			PopupNotification.emitter('error', t('emailConfirm.codeCreateError'));
		}
	};

	const onPinChange = async (value: string) => {
		setPinCode(value);
		if (value.length === codeLength) {
			setIsCodeBlocked(true);
			try {
				await backendApi.post('/v2/users/confirm', {
					code: value,
				});
				User.updateEmailConfirmed(true);
				PopupNotification.emitter('success', t('emailConfirm.pinSuccess'));
				onSuccess?.();
				closeHandler();
			} catch (e) {
				const { response } = e as AxiosError;
				const codeInputDelay = (response?.data as ICodeDelayTimeout)?.nextTryToConfirmInSeconds;
				onError?.();
				if (codeInputDelay) {
					PopupNotification.emitter('error', t('emailConfirm.pinWrong'));
				} else {
					PopupNotification.emitter('error', t('emailConfirm.pinError'));
				}
				setPinCode('');
				setTimeout(() => setIsCodeBlocked(false), (codeInputDelay || 0.1) * 1000);
			}
		}
	};

	useEffect(() => {
		if (isOpen && !codeRequestDelay) {
			handlePinRequest();
		}
	}, [isOpen]);

	useEffect(() => {
		let timeout: NodeJS.Timeout;

		if (codeRequestDelay) {
			timeout = setTimeout(() => {
				setCodeRequestDelay(0);
				setIsAgainBlocked(false);
			}, codeRequestDelay * 1000);
		}

		return () => clearTimeout(timeout);
	}, [codeRequestDelay]);

	return (
		<Modal
			open={isOpen}
			setOpen={closeHandler}
			disableScrollArea
			footer={
				<Button
					data-testid="confirm-email-modal-send-code-button"
					disabled={isAgainBlocked}
					onClick={handlePinRequest}
				>
					{t('emailConfirm.again')}
				</Button>
			}
			closeButton={
				<Button variant="secondary" onClick={() => onCloseHandler}>
					{t('common.exit')}
				</Button>
			}
		>
			<div data-testid="confirm-email-modal" className="text-brand-dark-blue text-2xl mb-2 mt-[32px]">
				{t('emailConfirm.title')}
			</div>
			<div className="text-brand-gray-2 text-sm mb-5">
				{t('emailConfirm.sendEmail')}
				<span className="text-brand-dark-blue"> {User.profile?.email ?? ''}</span>
				{t('emailConfirm.seePin')}
				<div className="flex mt-5 gap-5 px-4 py-3 bg-brand-gray-5 text-brand-gray-2 rounded-xl">
					<div>
						<div className="flex size-10 justify-center items-center bg-white aspect-square rounded-[4px]">
							<Icon name="info" size={20} />
						</div>
					</div>
					<div className="text-sm">{t('emailConfirm.codeInfo')}</div>
				</div>
			</div>
			<div className="text-brand-dark-blue text-sm font-semibold mb-1">{t('emailConfirm.enterPin')}</div>
			<div className="mb-5">
				<InputOTP
					name="confirm-email-modal-number-input"
					maxLength={codeLength}
					value={pinCode}
					disabled={isCodeBlocked}
					onChange={onPinChange}
				>
					<InputOTPGroup className="flex gap-1">
						{[...Array(codeLength).keys()].map((i) => (
							<InputOTPSlot key={i} index={i} className="border rounded-md" />
						))}
						{isCodeBlocked && <Loader2 className="animate-spin" />}
					</InputOTPGroup>
				</InputOTP>
			</div>
		</Modal>
	);
};

export { ConfirmEmailModal };
